// 从 shared 包导入静态路径处理函数
import { resolveStaticPath } from '@packages/shared';

/**
 * 合并 CSS 类名
 */
export function cn(...inputs: (string | undefined | null | boolean)[]) {
  return inputs.filter(Boolean).join(' ');
}

/**
 * 格式化日期
 */
export function formatDate(
  date: string | Date,
  format = 'YYYY-MM-DD HH:mm:ss'
): string {
  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');

  return format
    .replace('YYYY', year.toString())
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
}

/**
 * 防抖函数
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * 节流函数
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * 深拷贝
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as T;
  }

  if (obj instanceof Array) {
    return obj.map(item => deepClone(item)) as T;
  }

  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }

  return obj;
}

/**
 * 获取静态文件路径的便捷方法
 * 专门为 template-app 应用定制
 * @param path 原始路径，例如：/template-app/static-files/1.txt
 * @returns 处理后的路径
 */
export function getStaticPath(path: string): string {
  return resolveStaticPath(path, 'ix');
}

/**
 * 静态文件路径使用示例
 */
export const staticPathExamples = {
  // 示例1：文本文件
  textFile: () => getStaticPath('/template-app/static-files/1.txt'),

  // 示例2：图片文件
  imageFile: () => getStaticPath('/template-app/static-files/images/logo.png'),

  // 示例3：配置文件
  configFile: () => getStaticPath('/template-app/static-config/app.json'),

  // 示例4：CSS文件
  cssFile: () => getStaticPath('/template-app/static-files/styles/custom.css'),

  // 示例5：JavaScript文件
  jsFile: () => getStaticPath('/template-app/static-files/scripts/utils.js'),
};
